import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useTheme } from '@mui/material/styles';
import { useTheme as useCustomTheme } from '../context/ThemeContext';

export const ThreeBackground: React.FC = () => {
  const theme = useTheme();
  const { activeThemeDefinition } = useCustomTheme();
  
  // Récupérer la configuration 3D du thème
  const threeConfig = (activeThemeDefinition?.threeJs || {
    enabled: true,
    style: 'both',
    waveSpeed: 0.5,
    particleCount: 800,
  }) as {
    enabled: boolean;
    style: 'grid' | 'particles' | 'both';
    waveSpeed: number;
    particleCount: number;
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Références pour modifier dynamiquement les couleurs de base si le thème change
  const gridHelperRef = useRef<THREE.LineSegments | null>(null);
  const borderMaterialRef = useRef<THREE.LineBasicMaterial | null>(null);

  useEffect(() => {
    if (gridHelperRef.current) {
      const helper = gridHelperRef.current;
      (helper.material as THREE.LineBasicMaterial).color.set(theme.palette.primary.main);
    }
    if (borderMaterialRef.current) {
      borderMaterialRef.current.color.set(theme.palette.primary.main);
    }
  }, [theme.palette.primary.main]);

  useEffect(() => {
    // Si la 3D est désactivée, ne rien faire
    if (threeConfig.enabled === false) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    // 1. Initialiser le renderer, la scène et la caméra
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    
    // Position de caméra inclinée vers le bas et orientée à 15° par rapport à la grille de jeu
    camera.position.set(20.705, 38, 72.274);
    camera.lookAt(0, -6, -5);

    const clock = new THREE.Clock();

    // Limites du terrain de jeu (Arène Tron)
    const gridWidth = 160;
    const gridDepth = 110;
    
    // Variables pour l'interactivité de la caméra
    let mouseX = 0;
    let mouseY = 0;
    let targetCameraX = 0;
    let targetCameraY = 38;
    let targetCameraZ = 75;
    // 2. Grille de jeu type TRON rectangulaire (sans dépassement et sans diagonales)
    const buildRectangularGrid = (width: number, depth: number, step: number, colorStr: string) => {
      const pts = [];
      const halfW = width / 2;
      const halfD = depth / 2;
      
      // Lignes horizontales (parallèles à l'axe X)
      for (let z = -halfD; z <= halfD; z += step) {
        pts.push(new THREE.Vector3(-halfW, -10, z));
        pts.push(new THREE.Vector3(halfW, -10, z));
      }
      
      // Lignes verticales (parallèles à l'axe Z)
      for (let x = -halfW; x <= halfW; x += step) {
        pts.push(new THREE.Vector3(x, -10, -halfD));
        pts.push(new THREE.Vector3(x, -10, halfD));
      }
      
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({
        color: new THREE.Color(colorStr),
        transparent: true,
        opacity: 0.18
      });
      
      return new THREE.LineSegments(geo, mat);
    };

    const gridSegments = buildRectangularGrid(gridWidth, gridDepth, 4, theme.palette.primary.main);
    scene.add(gridSegments);
    // @ts-ignore
    gridHelperRef.current = gridSegments;

    // 3. Bordures néon de l'arène (sans diagonales)
    const borderPoints = [];
    const halfW = gridWidth / 2;
    const halfD = gridDepth / 2;
    borderPoints.push(new THREE.Vector3(-halfW, -10, -halfD));
    borderPoints.push(new THREE.Vector3(halfW, -10, -halfD));
    borderPoints.push(new THREE.Vector3(halfW, -10, halfD));
    borderPoints.push(new THREE.Vector3(-halfW, -10, halfD));
    
    const borderGeo = new THREE.BufferGeometry().setFromPoints(borderPoints);
    const borderMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(theme.palette.primary.main),
      transparent: true,
      opacity: 0.45
    });
    const border = new THREE.LineLoop(borderGeo, borderMat);
    scene.add(border);
    borderMaterialRef.current = borderMat;

    // 4. Champ de particules ambiantes (poussière cyber)
    let particles: THREE.Points | null = null;
    const renderParticles = false; // désactivé à la demande de l'utilisateur
    const particleCount = threeConfig.particleCount ?? 800;

    const createCircleTexture = (colorStr: string) => {
      const size = 32;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = size;
      tempCanvas.height = size;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        const gradient = ctx.createRadialGradient(
          size / 2, size / 2, 0,
          size / 2, size / 2, size / 2
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
        gradient.addColorStop(0.2, colorStr);
        gradient.addColorStop(0.5, 'rgba(0, 50, 150, 0.15)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
      }
      return new THREE.CanvasTexture(tempCanvas);
    };

    if (renderParticles) {
      const particleGeo = new THREE.BufferGeometry();
      const posArray = new Float32Array(particleCount * 3);
      const velocities = new Float32Array(particleCount * 3);

      for (let i = 0; i < particleCount * 3; i += 3) {
        posArray[i] = (Math.random() - 0.5) * 160;
        posArray[i + 1] = (Math.random() - 0.1) * 60; // au-dessus du sol
        posArray[i + 2] = (Math.random() - 0.5) * 110;

        velocities[i] = (Math.random() - 0.5) * 0.015;
        velocities[i + 1] = (Math.random() * 0.01) + 0.003;
        velocities[i + 2] = (Math.random() - 0.5) * 0.015;
      }

      particleGeo.setAttribute('position', new THREE.BufferAttribute(posArray, 3));

      const particleMat = new THREE.PointsMaterial({
        size: 1.5,
        color: new THREE.Color(theme.palette.secondary.main),
        transparent: true,
        opacity: 0.75,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        map: createCircleTexture(theme.palette.secondary.main),
      });

      particles = new THREE.Points(particleGeo, particleMat);
      scene.add(particles);

      (particles as any).tick = () => {
        if (!particles) return;
        const positions = particles.geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < particleCount * 3; i += 3) {
          positions[i] += velocities[i];
          positions[i + 1] += velocities[i + 1];
          positions[i + 2] += velocities[i + 2];

          if (positions[i + 1] > 40) {
            positions[i + 1] = -10;
            positions[i] = (Math.random() - 0.5) * 160;
          }
        }
        particles.geometry.attributes.position.needsUpdate = true;
      };
    }

    // 5. CONFIGURATION ET LOGIQUE DES MOTOS TRON
    interface Bike {
      id: number;
      color: string; // Couleur brillante (trail)
      chassisColor: string; // Couleur sombre (châssis)
      position: { x: number; z: number };
      direction: { dx: number; dz: number };
      mesh: THREE.Group;
      trailMeshList: THREE.Mesh[];
      currentTrailMesh: THREE.Mesh | null;
      glowMesh: THREE.Mesh | null;
      trailTexture: THREE.Texture;
      lastPoint: { x: number; z: number };
      trailHistory: Array<{ x1: number; z1: number; x2: number; z2: number }>;
      speed: number;
      alive: boolean;
      respawnTimer: number;
      startSideIndex: number;
      lastTurnTime: number;
    }

    const bikes: Bike[] = [];
    const renderGrid = threeConfig.style === 'both' || threeConfig.style === 'grid';

    // Créer un maillage 3D de moto TRON aérodynamique (inspiré de Tron: Legacy avec jantes creuses et carénage enveloppant)
    const createBikeMesh = (chassisColorStr: string, trailColorStr: string) => {
      const group = new THREE.Group();
      
      const chassisMat = new THREE.MeshBasicMaterial({ color: chassisColorStr });
      const neonMat = new THREE.MeshBasicMaterial({ color: trailColorStr });
      const glassMat = new THREE.MeshBasicMaterial({ color: '#15151c' });
      const hubMat = new THREE.MeshBasicMaterial({ color: '#1d1d22' });
      
      // 1. Carénage principal enveloppant (Sombre)
      const bodyGeo = new THREE.BoxGeometry(2.3, 0.8, 0.41);
      const body = new THREE.Mesh(bodyGeo, chassisMat);
      body.position.y = 0.6;
      group.add(body);
      
      // 2. Cockpit aérodynamique profilé (Verre fumé)
      const cockGeo = new THREE.BoxGeometry(1.1, 0.45, 0.36);
      const cockpit = new THREE.Mesh(cockGeo, glassMat);
      cockpit.position.set(0, 0.95, 0);
      group.add(cockpit);

      // 3. Capot supérieur de couleur (Accent néon)
      const hoodGeo = new THREE.BoxGeometry(1.6, 0.25, 0.42);
      const hood = new THREE.Mesh(hoodGeo, neonMat);
      hood.position.set(0.1, 0.9, 0);
      group.add(hood);

      // 4. Lignes néon décoratives latérales (Bandes lumineuses sur les flancs)
      const sideNeonGeo = new THREE.BoxGeometry(1.8, 0.06, 0.43); // dépasse de 0.01 sur les côtés (car corps = 0.41)
      const sideNeon = new THREE.Mesh(sideNeonGeo, neonMat);
      sideNeon.position.set(0, 0.5, 0);
      group.add(sideNeon);

      // 5. Ligne néon supérieure (Spine)
      const topNeonGeo = new THREE.BoxGeometry(1.4, 0.05, 0.075);
      const topNeon = new THREE.Mesh(topNeonGeo, neonMat);
      topNeon.position.set(0.1, 1.03, 0);
      group.add(topNeon);

      // Géométries de roues (jante extérieure brillante + intérieur vide/sombre)
      const wheelGeo = new THREE.CylinderGeometry(0.55, 0.55, 0.45, 12);
      wheelGeo.rotateX(Math.PI / 2);
      const hubGeo = new THREE.CylinderGeometry(0.38, 0.38, 0.46, 12);
      hubGeo.rotateX(Math.PI / 2);

      // 6. Roue Avant (Jante brillante + moyeu creux)
      const frontWheelRim = new THREE.Mesh(wheelGeo, neonMat);
      frontWheelRim.position.set(0.8, 0.55, 0);
      group.add(frontWheelRim);

      const frontWheelHub = new THREE.Mesh(hubGeo, hubMat);
      frontWheelHub.position.set(0.8, 0.55, 0);
      group.add(frontWheelHub);

      // 7. Roue Arrière (Jante brillante + moyeu creux)
      const backWheelRim = new THREE.Mesh(wheelGeo, neonMat);
      backWheelRim.position.set(-0.8, 0.55, 0);
      group.add(backWheelRim);

      const backWheelHub = new THREE.Mesh(hubGeo, hubMat);
      backWheelHub.position.set(-0.8, 0.55, 0);
      group.add(backWheelHub);

      return group;
    };

    // Utilitaire de conversion de couleur Hex en RGBA
    const hexToRgba = (hex: string, alpha: number) => {
      const cleanHex = hex.replace('#', '');
      const r = parseInt(cleanHex.substring(0, 2), 16);
      const g = parseInt(cleanHex.substring(2, 4), 16);
      const b = parseInt(cleanHex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Créer la texture de dégradé blanc (glow) partagée
    const createGlowTexture = () => {
      const w = 256;
      const h = 128;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.Texture();
      
      // Dégradé horizontal blanc
      const gradBody = ctx.createLinearGradient(0, 0, w, 0);
      gradBody.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradBody.addColorStop(1, 'rgba(255, 255, 255, 0.42)'); // Fond blanc soft
      ctx.fillStyle = gradBody;
      ctx.fillRect(0, 0, w, h);
      
      // Bordures horizontales blanches brillantes s'estompant vers la gauche
      const gradBorder = ctx.createLinearGradient(0, 0, w, 0);
      gradBorder.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradBorder.addColorStop(1, 'rgba(255, 255, 255, 0.98)');
      
      ctx.strokeStyle = gradBorder;
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(0, 5);
      ctx.lineTo(w, 5);
      ctx.moveTo(0, h - 5);
      ctx.lineTo(w, h - 5);
      ctx.stroke();
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      return texture;
    };

    const sharedGlowTexture = createGlowTexture();

    // Créer une texture canvas pour donner du style aux traînées de lumière (murs)
    const createTrailTexture = (colorStr: string) => {
      const width = 128;
      const height = 128;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.Texture();

      // 1. Fond semi-translucide coloré
      ctx.fillStyle = hexToRgba(colorStr, 0.22);
      ctx.fillRect(0, 0, width, height);

      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, hexToRgba(colorStr, 0.05));
      grad.addColorStop(0.5, hexToRgba(colorStr, 0.45));
      grad.addColorStop(1, hexToRgba(colorStr, 0.15));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // 2. Lignes verticales de lumière (traits verticaux droits comme dans le film)
      ctx.strokeStyle = hexToRgba(colorStr, 0.45);
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();

      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();

      // 3. Bordures néon horizontales
      ctx.strokeStyle = colorStr;
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(0, 5);
      ctx.lineTo(width, 5);
      ctx.moveTo(0, height - 5);
      ctx.lineTo(width, height - 5);
      ctx.stroke();

      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      return texture;
    };

    // Commencer un nouveau segment de traînée lumineuse
    const startNewTrailSegment = (b: Bike) => {
      // 1. Mur de base (Texture répétée fixe en arrière, clonée pour avoir un repeat indépendant)
      const trailMat = new THREE.MeshBasicMaterial({
        map: b.trailTexture.clone(),
        transparent: true,
        opacity: 0.95,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const trailGeo = new THREE.PlaneGeometry(1.0, 1.2);
      const mesh = new THREE.Mesh(trailGeo, trailMat);
      mesh.position.y = -9.4;
      scene.add(mesh);
      b.currentTrailMesh = mesh;
      b.lastPoint = { ...b.position };

      // 2. Overlay de glow blanc qui suit la moto (AdditiveBlending, cloné pour chaque moto)
      const glowMat = new THREE.MeshBasicMaterial({
        map: sharedGlowTexture.clone(),
        transparent: true,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const glowGeo = new THREE.PlaneGeometry(1.0, 1.22); // légèrement plus haut pour chevaucher les bordures
      const glowMesh = new THREE.Mesh(glowGeo, glowMat);
      glowMesh.position.y = -9.4;
      scene.add(glowMesh);
      b.glowMesh = glowMesh;
    };

    // Mettre à jour l'étirement du segment de traînée courante
    const updateCurrentTrail = (b: Bike) => {
      if (!b.currentTrailMesh || !b.glowMesh) return;
      const x1 = b.lastPoint.x;
      const z1 = b.lastPoint.z;
      const x2 = b.position.x;
      const z2 = b.position.z;
      
      const dx = x2 - x1;
      const dz = z2 - z1;
      const length = Math.sqrt(dx * dx + dz * dz) + 0.01;
      
      const mx = (x1 + x2) / 2;
      const mz = (z1 + z2) / 2;
      
      // Mettre à jour le mur de base
      b.currentTrailMesh.position.set(mx, -9.4, mz);
      const angle = Math.atan2(-dz, dx);
      b.currentTrailMesh.rotation.y = angle;
      b.currentTrailMesh.scale.set(length, 1, 1);

      // Garder les motifs de la texture de base parfaitement fixes en adaptant sa répétition
      const trailHeight = 1.2;
      const mat = b.currentTrailMesh.material as THREE.MeshBasicMaterial;
      if (mat.map) {
        mat.map.repeat.set(length / trailHeight, 1);
      }

      // Mettre à jour l'overlay de glow blanc (il épouse exactement la géométrie du mur)
      b.glowMesh.position.set(mx, -9.4, mz);
      b.glowMesh.rotation.y = angle;
      b.glowMesh.scale.set(length, 1, 1);

      // Ajuster le décalage et la répétition pour maintenir la taille physique du glow à 15 unités constantes
      const glowMat = b.glowMesh.material as THREE.MeshBasicMaterial;
      if (glowMat.map) {
        const glowRepeatX = length / 15.0;
        glowMat.map.repeat.set(glowRepeatX, 1);
        glowMat.map.offset.set(1.0 - glowRepeatX, 0); // Décale pour garder le blanc ancré sur la moto à droite (U=1)
      }
    };

    // Archiver la traînée courante dans l'historique lors d'un virage
    const archiveCurrentTrail = (b: Bike) => {
      if (!b.currentTrailMesh) return;
      const x1 = b.lastPoint.x;
      const z1 = b.lastPoint.z;
      const x2 = b.position.x;
      const z2 = b.position.z;
      
      b.trailHistory.push({ x1, z1, x2, z2 });
      b.trailMeshList.push(b.currentTrailMesh);

      // Retirer l'effet glow du segment archivé
      if (b.glowMesh) {
        scene.remove(b.glowMesh);
        b.glowMesh.geometry.dispose();
        const mat = b.glowMesh.material as THREE.MeshBasicMaterial;
        if (mat.map) mat.map.dispose(); // Disposer du clone du glow !
        mat.dispose();
        b.glowMesh = null;
      }
      
      startNewTrailSegment(b);
    };

    // Nettoyer les objets 3D de traînée
    const clearBikeTrails = (b: Bike) => {
      if (b.currentTrailMesh) {
        scene.remove(b.currentTrailMesh);
        const mat = b.currentTrailMesh.material as THREE.MeshBasicMaterial;
        if (mat.map) mat.map.dispose(); // Disposer du clone
        b.currentTrailMesh.geometry.dispose();
        mat.dispose();
        b.currentTrailMesh = null;
      }
      if (b.glowMesh) {
        scene.remove(b.glowMesh);
        b.glowMesh.geometry.dispose();
        const mat = b.glowMesh.material as THREE.MeshBasicMaterial;
        if (mat.map) mat.map.dispose(); // Disposer du clone du glow !
        mat.dispose();
        b.glowMesh = null;
      }
      b.trailMeshList.forEach(mesh => {
        scene.remove(mesh);
        mesh.geometry.dispose();
        const mat = mesh.material as THREE.MeshBasicMaterial;
        if (mat.map) mat.map.dispose(); // Disposer du clone
        mat.dispose();
      });
      b.trailMeshList = [];
      b.trailHistory = [];
    };
    const spawnPoints = [
      { side: 0, x: -80 + 3.0, z: 0, dx: 1, dz: 0 },   // Milieu gauche (dirigé vers le centre / droite)
      { side: 1, x: 80 - 3.0, z: 0, dx: -1, dz: 0 },   // Milieu droit (dirigé vers le centre / gauche)
      { side: 2, x: 0, z: -55 + 3.0, dx: 0, dz: 1 },   // Milieu haut (dirigé vers le centre / bas)
      { side: 3, x: 0, z: 55 - 3.0, dx: 0, dz: -1 }    // Milieu bas (dirigé vers le centre / haut)
    ];

    // Trouver un côté de départ libre qui n'est occupé par aucun concurrent en vie
    const getFreeStartSide = (currentBikeId: number) => {
      const occupiedSides = bikes
        .filter(b => b.alive && b.id !== currentBikeId)
        .map(b => b.startSideIndex);
      const freeSides = [0, 1, 2, 3].filter(side => !occupiedSides.includes(side));
      if (freeSides.length > 0) {
        return freeSides[Math.floor(Math.random() * freeSides.length)];
      }
      return currentBikeId % 4;
    };

    // Configuration stricte des 4 couleurs de traînées (traits lumineux brillants)
    const trailColors = [
      '#00f0ff', // Bleu (Cyan TRON)
      '#ff003c', // Rouge
      '#ffd200', // Jaune
      '#00ff66'  // Vert
    ];

    // Configuration des 4 couleurs de châssis correspondantes (motos physiques plus sombres)
    const chassisColors = [
      '#003c66', // Bleu sombre
      '#660018', // Rouge sombre
      '#665400', // Jaune sombre
      '#006629'  // Vert sombre
    ];

    // Initialisation des 4 motos (chacune sur un côté différent)
    if (renderGrid) {
      for (let i = 0; i < 4; i++) {
        const sideIdx = i; // Chaque moto prend un côté de départ unique (0, 1, 2, 3)
        const sp = spawnPoints[sideIdx];
        const trailColor = trailColors[i];
        const chassisColor = chassisColors[i];
        const mesh = createBikeMesh(chassisColor, trailColor);
        mesh.position.set(sp.x, -10, sp.z);
        scene.add(mesh);

        // Pré-générer la texture de traînée pour cette moto
        const trailTexture = createTrailTexture(trailColor);

        const bike: Bike = {
          id: i,
          color: trailColor,
          chassisColor,
          position: { x: sp.x, z: sp.z },
          direction: { dx: sp.dx, dz: sp.dz },
          mesh,
          trailMeshList: [],
          currentTrailMesh: null,
          glowMesh: null,
          trailTexture,
          lastPoint: { x: sp.x, z: sp.z },
          trailHistory: [],
          speed: 0.45,
          alive: true,
          respawnTimer: 0,
          startSideIndex: sideIdx,
          lastTurnTime: 0
        };

        startNewTrailSegment(bike);
        bikes.push(bike);
      }
    }

    // Algorithmes de collision
    const isPointNearSegment = (px: number, pz: number, x1: number, z1: number, x2: number, z2: number, t: number) => {
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minZ = Math.min(z1, z2);
      const maxZ = Math.max(z1, z2);
      
      if (Math.abs(x1 - x2) < 0.01) {
        // Vertical segment
        if (pz >= minZ - t && pz <= maxZ + t) {
          if (Math.abs(px - x1) < t) return true;
        }
      } else {
        // Horizontal segment
        if (px >= minX - t && px <= maxX + t) {
          if (Math.abs(pz - z1) < t) return true;
        }
      }
      return false;
    };

    // Détection de collision avec les murs extérieurs de la grille
    const checkWallCollision = (x: number, z: number) => {
      const margin = 2.0;
      return (
        x > (gridWidth / 2) - margin ||
        x < -(gridWidth / 2) + margin ||
        z > (gridDepth / 2) - margin ||
        z < -(gridDepth / 2) + margin
      );
    };

    // Détection de collision STRICTEMENT avec les traînées de lumière
    const checkTrailCollision = (x: number, z: number, ignoreOwnBikeId: number) => {
      const threshold = 0.8;
      
      for (const b of bikes) {
        if (!b.alive) continue;
        
        const isOwnBike = b.id === ignoreOwnBikeId;
        
        // Si c'est notre propre moto, on IGNORE les 2 derniers segments archivés de son historique
        // afin de ne pas déclencher de crash immédiat sur le virage qu'on vient de prendre.
        const historyToCheck = isOwnBike 
          ? b.trailHistory.slice(0, -2) 
          : b.trailHistory;
          
        for (const seg of historyToCheck) {
          if (isPointNearSegment(x, z, seg.x1, seg.z1, seg.x2, seg.z2, threshold)) {
            return true;
          }
        }
        
        // Tester le segment actif courant (uniquement pour les autres motos)
        if (!isOwnBike && b.currentTrailMesh) {
          if (isPointNearSegment(x, z, b.lastPoint.x, b.lastPoint.z, b.position.x, b.position.z, threshold)) {
            return true;
          }
        }
      }
      
      return false;
    };


    // 6. Système d'explosions (Rendu en lignes radiales comme dans le film original Tron de 1982)
    interface LineExplosion {
      lineSegments: THREE.LineSegments;
      directions: Float32Array;
      speeds: Float32Array;
      currentT: number;
      opacity: number;
      tick: () => boolean;
    }

    const activeExplosions: LineExplosion[] = [];

    const spawnExplosion = (x: number, z: number, colorStr: string) => {
      const rayCount = 35;
      const positions = new Float32Array(rayCount * 2 * 3); // 2 points par segment, 3 coordonnées par point
      const directions = new Float32Array(rayCount * 3);
      const speeds = new Float32Array(rayCount);

      const centerY = -10.0; // Niveau du sol

      for (let i = 0; i < rayCount; i++) {
        // Direction radiale en forme de dôme 3D (vers le haut)
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random()); // phi entre 0 et PI/2 -> Y toujours positif !
        
        directions[i * 3] = Math.sin(phi) * Math.cos(theta);
        directions[i * 3 + 1] = Math.cos(phi);
        directions[i * 3 + 2] = Math.sin(phi) * Math.sin(theta);

        speeds[i] = 1.0 + Math.random() * 1.8;

        // Position initiale (tous les segments comprimés au centre)
        positions[i * 6] = x;
        positions[i * 6 + 1] = centerY;
        positions[i * 6 + 2] = z;
        positions[i * 6 + 3] = x;
        positions[i * 6 + 4] = centerY;
        positions[i * 6 + 5] = z;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const mat = new THREE.LineBasicMaterial({
        color: 0xffffff, // Blanc brillant vectoriel conforme au film original
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      });

      const lineSegments = new THREE.LineSegments(geo, mat);
      scene.add(lineSegments);

      const expObj: LineExplosion = {
        lineSegments,
        directions,
        speeds,
        currentT: 0,
        opacity: 1.0,
        tick: () => {
          expObj.currentT += 0.28; // vitesse de progression
          expObj.opacity -= 0.024; // s'estompe progressivement

          mat.opacity = expObj.opacity;

          if (expObj.opacity <= 0) {
            scene.remove(lineSegments);
            geo.dispose();
            mat.dispose();
            return false;
          }

          const posAttr = geo.attributes.position;
          const arr = posAttr.array as Float32Array;

          for (let i = 0; i < rayCount; i++) {
            const dx = directions[i * 3];
            const dy = directions[i * 3 + 1];
            const dz = directions[i * 3 + 2];
            const sp = speeds[i];

            const t = expObj.currentT * sp;

            // Le segment de rayon lumineux s'étend et se déplace vers l'extérieur
            const startDist = t * 0.7;
            arr[i * 6] = x + dx * startDist;
            arr[i * 6 + 1] = centerY + dy * startDist;
            arr[i * 6 + 2] = z + dz * startDist;

            const endDist = t * 2.0;
            arr[i * 6 + 3] = x + dx * endDist;
            arr[i * 6 + 4] = centerY + dy * endDist;
            arr[i * 6 + 5] = z + dz * endDist;
          }

          posAttr.needsUpdate = true;
          return true;
        }
      };

      activeExplosions.push(expObj);
    };

    // 6.5. Système de disparition progressive des traînées (enfoncement et fondu dans la grille)
    interface SinkingTrail {
      meshes: THREE.Mesh[];
      opacity: number;
      yOffset: number;
      tick: () => boolean;
    }

    const activeSinkingTrails: SinkingTrail[] = [];

    const spawnSinkingTrail = (b: Bike) => {
      const meshesToSink: THREE.Mesh[] = [];
      
      // 1. Récupérer le segment actif
      if (b.currentTrailMesh) {
        meshesToSink.push(b.currentTrailMesh);
        b.currentTrailMesh = null;
      }
      
      // 2. Récupérer le glow actif
      if (b.glowMesh) {
        meshesToSink.push(b.glowMesh);
        b.glowMesh = null;
      }
      
      // 3. Récupérer les segments archivés
      b.trailMeshList.forEach(mesh => {
        meshesToSink.push(mesh);
      });
      b.trailMeshList = [];
      b.trailHistory = [];

      const sinkObj: SinkingTrail = {
        meshes: meshesToSink,
        opacity: 0.95,
        yOffset: -9.4, // niveau initial du sol
        tick: () => {
          sinkObj.opacity -= 0.035; // s'estompe en environ 27 frames (~0.45s)
          sinkObj.yOffset -= 0.08;   // s'enfonce sous la grille

          if (sinkObj.opacity <= 0) {
            // Nettoyage complet des meshes et matériaux de cette traînée
            sinkObj.meshes.forEach(mesh => {
              scene.remove(mesh);
              mesh.geometry.dispose();
              const mat = mesh.material as THREE.MeshBasicMaterial;
              if (mat.map) mat.map.dispose();
              mat.dispose();
            });
            return false;
          }

          // Mettre à jour chaque mesh
          sinkObj.meshes.forEach(mesh => {
            mesh.position.y = sinkObj.yOffset;
            const mat = mesh.material as THREE.MeshBasicMaterial;
            mat.opacity = sinkObj.opacity;
          });

          return true;
        }
      };

      activeSinkingTrails.push(sinkObj);
    };

    // Utilitaire pour créer un nouveau maillage de segment de traînée archivé lors d'une scission
    const createHistorySegmentMesh = (b: Bike, x1: number, z1: number, x2: number, z2: number) => {
      const dx = x2 - x1;
      const dz = z2 - z1;
      const length = Math.hypot(dx, dz);
      if (length < 0.05) return null;

      const trailMat = new THREE.MeshBasicMaterial({
        map: b.trailTexture.clone(),
        transparent: true,
        opacity: 0.95,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const trailGeo = new THREE.PlaneGeometry(1.0, 1.2);
      const mesh = new THREE.Mesh(trailGeo, trailMat);
      mesh.position.y = -9.4;
      scene.add(mesh);

      const mx = (x1 + x2) / 2;
      const mz = (z1 + z2) / 2;
      mesh.position.set(mx, -9.4, mz);
      const angle = Math.atan2(-dz, dx);
      mesh.rotation.y = angle;
      mesh.scale.set(length, 1, 1);
      if (trailMat.map) {
        trailMat.map.repeat.set(length / 1.2, 1);
      }
      return mesh;
    };

    // Créer un trou physique et visuel dans une traînée de lumière lors d'une collision
    const createHoleInTrail = (cx: number, cz: number, ignoreOwnBikeId: number) => {
      const threshold = 0.8;
      const gap = 2.0; // Rayon du trou (largeur totale de 4.0 unités de passage)

      for (const other of bikes) {
        // 1. Si l'active segment d'un autre joueur est touché, on l'archive d'abord pour pouvoir le scinder
        if (other.id !== ignoreOwnBikeId && other.currentTrailMesh) {
          if (isPointNearSegment(cx, cz, other.lastPoint.x, other.lastPoint.z, other.position.x, other.position.z, threshold)) {
            archiveCurrentTrail(other);
          }
        }

        // 2. Parcourir l'historique des segments
        const historyToCheck = other.id === ignoreOwnBikeId 
          ? other.trailHistory.slice(0, -2) 
          : other.trailHistory;

        for (let i = historyToCheck.length - 1; i >= 0; i--) {
          const seg = historyToCheck[i];
          if (isPointNearSegment(cx, cz, seg.x1, seg.z1, seg.x2, seg.z2, threshold)) {
            const dx = seg.x2 - seg.x1;
            const dz = seg.z2 - seg.z1;
            const L = Math.hypot(dx, dz);
            if (L < 0.1) continue;

            const ux = dx / L;
            const uz = dz / L;

            // Projection du point de collision sur l'axe du segment
            const d = (cx - seg.x1) * ux + (cz - seg.z1) * uz;

            const historyToAdd: { x1: number, z1: number, x2: number, z2: number }[] = [];
            const meshToAdd: THREE.Mesh[] = [];

            // Segment de gauche (avant le trou)
            if (d - gap > 0.5) {
              const px1 = seg.x1;
              const pz1 = seg.z1;
              const px2 = seg.x1 + ux * (d - gap);
              const pz2 = seg.z1 + uz * (d - gap);
              const newMesh = createHistorySegmentMesh(other, px1, pz1, px2, pz2);
              if (newMesh) {
                historyToAdd.push({ x1: px1, z1: pz1, x2: px2, z2: pz2 });
                meshToAdd.push(newMesh);
              }
            }

            // Segment de droite (après le trou)
            if (L - (d + gap) > 0.5) {
              const px1 = seg.x1 + ux * (d + gap);
              const pz1 = seg.z1 + uz * (d + gap);
              const px2 = seg.x2;
              const pz2 = seg.z2;
              const newMesh = createHistorySegmentMesh(other, px1, pz1, px2, pz2);
              if (newMesh) {
                historyToAdd.push({ x1: px1, z1: pz1, x2: px2, z2: pz2 });
                meshToAdd.push(newMesh);
              }
            }

            // Supprimer l'ancien maillage et libérer la mémoire
            const mesh = other.trailMeshList[i];
            scene.remove(mesh);
            mesh.geometry.dispose();
            const mat = mesh.material as THREE.MeshBasicMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();

            other.trailHistory.splice(i, 1);
            other.trailMeshList.splice(i, 1);

            // Remplacer par les nouveaux segments scindés
            other.trailHistory.splice(i, 0, ...historyToAdd);
            other.trailMeshList.splice(i, 0, ...meshToAdd);

            return; // Scission terminée
          }
        }
      }
    };

    // 7. Écouteurs d'événements (Redimensionnement, souris, défilement)
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      targetCameraZ = 75 + scrollY * 0.03;
      targetCameraY = 38 - scrollY * 0.015;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // 8. Boucle d'animation principale
    let animationFrameId: number;
    let isTabVisible = true;

    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (!isTabVisible) return;

      // TICK 1: Particules ambiantes
      if (particles && (particles as any).tick) {
        (particles as any).tick();
      }

      // TICK 2: Explosions de crashs
      for (let i = activeExplosions.length - 1; i >= 0; i--) {
        const keepsGoing = activeExplosions[i].tick();
        if (!keepsGoing) {
          activeExplosions.splice(i, 1);
        }
      }

      // TICK 2.5: Traînées lumineuses qui s'enfoncent et s'estompent
      for (let i = activeSinkingTrails.length - 1; i >= 0; i--) {
        const keepsGoing = activeSinkingTrails[i].tick();
        if (!keepsGoing) {
          activeSinkingTrails.splice(i, 1);
        }
      }

      // TICK 3: Logique des motos TRON
      bikes.forEach(b => {
        if (!b.alive) {
          b.respawnTimer--;
          if (b.respawnTimer <= 0) {
            // Réapparition à un côté libre (aucun doublon de côté de départ)
            const sideIdx = getFreeStartSide(b.id);
            b.startSideIndex = sideIdx;
            const sp = spawnPoints[sideIdx];
            b.position.x = sp.x;
            b.position.z = sp.z;
            b.direction.dx = sp.dx;
            b.direction.dz = sp.dz;
            b.alive = true;
            b.mesh.visible = true;
            b.mesh.position.set(b.position.x, -10, b.position.z);
            b.lastTurnTime = 0;
            clearBikeTrails(b);
            startNewTrailSegment(b);
          }
          return;
        }

        // 1. Générer la grille d'occupation discrète fine-grain (résolution 1x1 unité) pour cette moto
        const cols = 160;
        const rows = 110;
        const grid = new Uint8Array(cols * rows);

        for (const other of bikes) {
          if (!other.alive) continue;
          const isOwn = other.id === b.id;
          
          // Pour la propre moto, on ignore les 2 derniers segments archivés pour la sécurité
          const history = isOwn ? other.trailHistory.slice(0, -2) : other.trailHistory;

          for (const seg of history) {
            const dx = seg.x2 - seg.x1;
            const dz = seg.z2 - seg.z1;
            const len = Math.hypot(dx, dz);
            const steps = Math.max(2, Math.floor(len));
            for (let s = 0; s <= steps; s++) {
              const px = seg.x1 + dx * (s / steps);
              const pz = seg.z1 + dz * (s / steps);
              const c = Math.floor(px + 80);
              const r = Math.floor(pz + 55);
              if (c >= 0 && c < cols && r >= 0 && r < rows) {
                grid[r * cols + c] = 1;
              }
            }
          }

          if (other.currentTrailMesh) {
            const dx = other.position.x - other.lastPoint.x;
            const dz = other.position.z - other.lastPoint.z;
            const len = Math.hypot(dx, dz);
            
            // Pour la propre moto, on s'arrête 3.0 unités avant sa position actuelle pour ne pas fausser le départ du BFS
            const maxDist = isOwn ? Math.max(0, len - 3.0) : len;
            const steps = Math.max(2, Math.floor(maxDist));
            for (let s = 0; s <= steps; s++) {
              const px = other.lastPoint.x + dx * (s / steps) * (maxDist / len);
              const pz = other.lastPoint.z + dz * (s / steps) * (maxDist / len);
              const c = Math.floor(px + 80);
              const r = Math.floor(pz + 55);
              if (c >= 0 && c < cols && r >= 0 && r < rows) {
                grid[r * cols + c] = 1;
              }
            }
          }
        }

        // Fonction locale BFS d'évaluation d'espace libre
        const evalBFS = (startX: number, startZ: number) => {
          const startCol = Math.floor(startX + 80);
          const startRow = Math.floor(startZ + 55);
          
          if (startCol < 0 || startCol >= cols || startRow < 0 || startRow >= rows) return 0;
          if (grid[startRow * cols + startCol] === 1) return 0;

          const tempGrid = new Uint8Array(grid);
          const queue: number[] = [startRow * cols + startCol];
          tempGrid[startRow * cols + startCol] = 1;
          let count = 0;
          let head = 0;

          while (head < queue.length) {
            const curr = queue[head++];
            count++;
            const currRow = Math.floor(curr / cols);
            const currCol = curr % cols;

            const neighbors = [
              { r: currRow + 1, c: currCol },
              { r: currRow - 1, c: currCol },
              { r: currRow, c: currCol + 1 },
              { r: currRow, c: currCol - 1 }
            ];

            for (const n of neighbors) {
              if (n.c >= 0 && n.c < cols && n.r >= 0 && n.r < rows) {
                const idx = n.r * cols + n.c;
                if (tempGrid[idx] === 0) {
                  tempGrid[idx] = 1;
                  queue.push(idx);
                }
              }
            }
          }
          return count;
        };

        // Indice de longement de mur (wall-hugging) pour économiser l'espace
        const evalWallHugging = (startX: number, startZ: number, dx: number, dz: number) => {
          let wallNeighbors = 0;
          const distances = [1.5, 3.5, 5.5];
          for (const d of distances) {
            const px = startX + dx * d;
            const pz = startZ + dz * d;
            const c = Math.floor(px + 80);
            const r = Math.floor(pz + 55);
            
            const offsets = [
              { dc: 1, dr: 0 },
              { dc: -1, dr: 0 },
              { dc: 0, dr: 1 },
              { dc: 0, dr: -1 }
            ];
            for (const offset of offsets) {
              const nc = c + offset.dc;
              const nr = r + offset.dr;
              if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) {
                wallNeighbors += 2; // Les bordures extérieures comptent double
              } else if (grid[nr * cols + nc] === 1) {
                wallNeighbors++;
              }
            }
          }
          return wallNeighbors;
        };

        // 2. Calculer les directions de virage potentielles (gauche et droite à 90°)
        let leftDir = { dx: 0, dz: 0 };
        let rightDir = { dx: 0, dz: 0 };
        if (b.direction.dz === 0) {
          leftDir = { dx: 0, dz: 1 };
          rightDir = { dx: 0, dz: -1 };
        } else {
          leftDir = { dx: -1, dz: 0 };
          rightDir = { dx: 1, dz: 0 };
        }

        // 3. Évaluer l'espace disponible (BFS) pour chaque option
        const evalDist = 3.5;
        const straightX = b.position.x + b.direction.dx * evalDist;
        const straightZ = b.position.z + b.direction.dz * evalDist;
        const straightScore = evalBFS(straightX, straightZ);

        const leftX = b.position.x + leftDir.dx * evalDist;
        const leftZ = b.position.z + leftDir.dz * evalDist;
        const leftScore = evalBFS(leftX, leftZ);

        const rightX = b.position.x + rightDir.dx * evalDist;
        const rightZ = b.position.z + rightDir.dz * evalDist;
        const rightScore = evalBFS(rightX, rightZ);

        // 4. Évaluer le comportement de longement de mur
        const straightHugging = evalWallHugging(b.position.x, b.position.z, b.direction.dx, b.direction.dz);
        const leftHugging = evalWallHugging(b.position.x, b.position.z, leftDir.dx, leftDir.dz);
        const rightHugging = evalWallHugging(b.position.x, b.position.z, rightDir.dx, rightDir.dz);

        // IA : Détecter les menaces de collision imminente en face
        const lookAheadDistance = b.speed * 12;
        const nextX = b.position.x + b.direction.dx * lookAheadDistance;
        const nextZ = b.position.z + b.direction.dz * lookAheadDistance;
        const isTrailAhead = checkTrailCollision(nextX, nextZ, b.id);
        const isWallAhead = checkWallCollision(nextX, nextZ);

        // IA : Force le virage si trop proche d'un mur extérieur
        const boundaryForceMargin = 5.0;
        let mustTurnBoundary = false;
        if (b.direction.dx > 0 && b.position.x > (gridWidth / 2) - boundaryForceMargin) mustTurnBoundary = true;
        else if (b.direction.dx < 0 && b.position.x < -(gridWidth / 2) + boundaryForceMargin) mustTurnBoundary = true;
        else if (b.direction.dz > 0 && b.position.z > (gridDepth / 2) - boundaryForceMargin) mustTurnBoundary = true;
        else if (b.direction.dz < 0 && b.position.z < -(gridDepth / 2) + boundaryForceMargin) mustTurnBoundary = true;

        const immediateThreat = isTrailAhead || isWallAhead || mustTurnBoundary;

        let executeTurn = false;
        let targetDir = { dx: b.direction.dx, dz: b.direction.dz };

        if (immediateThreat || straightScore < 15) {
          // Virage d'évitement d'urgence : choisir le côté avec le plus grand espace libre BFS
          executeTurn = true;
          if (leftScore > rightScore) {
            targetDir = leftDir;
          } else if (rightScore > leftScore) {
            targetDir = rightDir;
          } else {
            // Égalité : choisir la direction qui longe le plus les obstacles
            targetDir = leftHugging >= rightHugging ? leftDir : rightDir;
          }
        } else {
          // Pas de danger immédiat : comportement tactique & optimisation d'espace
          const bestScore = Math.max(leftScore, rightScore);
          
          // Si une option latérale offre beaucoup plus d'espace libre (ex: tunnel face à nous vs plaine sur le côté)
          if (bestScore > straightScore + 25) {
            executeTurn = true;
            targetDir = (leftScore === bestScore) ? leftDir : rightDir;
          }
          // Comportement intelligent de longement de mur (wall-hugging) pour économiser l'espace central
          else if (leftScore > 50 && leftHugging > straightHugging + 3 && leftHugging >= rightHugging) {
            executeTurn = true;
            targetDir = leftDir;
          } else if (rightScore > 50 && rightHugging > straightHugging + 3 && rightHugging >= leftHugging) {
            executeTurn = true;
            targetDir = rightDir;
          }
          // Sinon, chasser l'adversaire le plus proche de manière intelligente
          else if (Math.random() < 0.012) {
            let closestOpponent: Bike | null = null;
            let minDist = Infinity;
            for (const opp of bikes) {
              if (opp.id === b.id || !opp.alive) continue;
              const dist = Math.hypot(opp.position.x - b.position.x, opp.position.z - b.position.z);
              if (dist < minDist) {
                minDist = dist;
                closestOpponent = opp;
              }
            }

            if (closestOpponent) {
              const dLeft = Math.hypot(closestOpponent.position.x - leftX, closestOpponent.position.z - leftZ);
              const dRight = Math.hypot(closestOpponent.position.x - rightX, closestOpponent.position.z - rightZ);
              
              // On ne tourne vers l'adversaire que si le virage offre un espace de survie acceptable (> 30 cases BFS)
              if (dLeft < dRight && leftScore > 30) {
                executeTurn = true;
                targetDir = leftDir;
              } else if (dRight < dLeft && rightScore > 30) {
                executeTurn = true;
                targetDir = rightDir;
              }
            }
          }
        }

        // Exécuter le virage en validant la physique immédiate
        if (executeTurn && (targetDir.dx !== b.direction.dx || targetDir.dz !== b.direction.dz)) {
          // Double-vérification de collision physique à très courte portée (1.5 unités)
          const checkX = b.position.x + targetDir.dx * 1.5;
          const checkZ = b.position.z + targetDir.dz * 1.5;
          const safePhysically = !checkTrailCollision(checkX, checkZ, b.id) && !checkWallCollision(checkX, checkZ);
          if (safePhysically) {
            archiveCurrentTrail(b);
            b.direction.dx = targetDir.dx;
            b.direction.dz = targetDir.dz;
            b.lastTurnTime = performance.now(); // On garde pour trace mais sans cooldown bloquant
          }
        }

        // Avancer la moto
        b.position.x += b.direction.dx * b.speed;
        b.position.z += b.direction.dz * b.speed;

        // Clamp de sécurité absolu pour ne JAMAIS franchir la grille physique
        const clampMargin = 2.0;
        b.position.x = Math.max(-(gridWidth / 2) + clampMargin, Math.min((gridWidth / 2) - clampMargin, b.position.x));
        b.position.z = Math.max(-(gridDepth / 2) + clampMargin, Math.min((gridDepth / 2) - clampMargin, b.position.z));

        b.mesh.position.set(b.position.x, -10, b.position.z);
        const angle = Math.atan2(-b.direction.dz, b.direction.dx);
        b.mesh.rotation.y = angle;

        // Mettre à jour la traînée de lumière
        updateCurrentTrail(b);

        // Détection de collision : La moto n'explose QUE si elle entre en contact avec une traînée
        if (checkTrailCollision(b.position.x, b.position.z, b.id)) {
          // Boum !
          spawnExplosion(b.position.x, b.position.z, b.color);
          
          // Créer un trou physique dans la traînée de lumière percutée (scission du segment)
          createHoleInTrail(b.position.x, b.position.z, b.id);
          
          // Réapparition INSTANTANÉE (zéro délai de temporisation) !
          const sideIdx = getFreeStartSide(b.id);
          b.startSideIndex = sideIdx;
          const sp = spawnPoints[sideIdx];
          b.position.x = sp.x;
          b.position.z = sp.z;
          b.direction.dx = sp.dx;
          b.direction.dz = sp.dz;
          b.alive = true;
          b.mesh.visible = true;
          b.mesh.position.set(b.position.x, -10, b.position.z);
          b.lastTurnTime = 0;
          spawnSinkingTrail(b);
          startNewTrailSegment(b);
        }
      });

      // Parallaxe caméra douce
      targetCameraX = mouseX * 22;
      const currentTargetY = targetCameraY + (mouseY * 6);

      // Appliquer une rotation constante de 15° de la caméra autour du centre de l'arène (0, -6, -5)
      const rotY = 15 * Math.PI / 180;
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);

      // Calcul par rapport au centre de rotation
      const relX = targetCameraX;
      const relZ = targetCameraZ - (-5);

      const rotatedTargetX = relX * cosY + relZ * sinY;
      const rotatedTargetZ = -relX * sinY + relZ * cosY - 5;

      camera.position.x += (rotatedTargetX - camera.position.x) * 0.04;
      camera.position.y += (currentTargetY - camera.position.y) * 0.04;
      camera.position.z += (rotatedTargetZ - camera.position.z) * 0.04;
      camera.lookAt(0, -6, -5);

      renderer.render(scene, camera);
    };

    animate();

    // 9. Nettoyer les écouteurs et ressources WebGL au démontage
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      scene.remove(gridSegments);
      gridSegments.geometry.dispose();
      (gridSegments.material as THREE.Material).dispose();

      scene.remove(border);
      border.geometry.dispose();
      borderMat.dispose();

      if (particles) {
        scene.remove(particles);
        particles.geometry.dispose();
        if (Array.isArray(particles.material)) {
          particles.material.forEach(m => m.dispose());
        } else {
          particles.material.dispose();
        }
      }

      bikes.forEach(b => {
        scene.remove(b.mesh);
        // Libérer les roues et chassis
        b.mesh.children.forEach(child => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        clearBikeTrails(b);
        // Disposer de la texture spécifique
        b.trailTexture.dispose();
      });

      // Disposer de la texture de glow partagée
      sharedGlowTexture.dispose();

      activeExplosions.forEach(exp => {
        scene.remove(exp.lineSegments);
        exp.lineSegments.geometry.dispose();
        (exp.lineSegments.material as THREE.Material).dispose();
      });

      activeSinkingTrails.forEach(sink => {
        sink.meshes.forEach(mesh => {
          scene.remove(mesh);
          mesh.geometry.dispose();
          const mat = mesh.material as THREE.MeshBasicMaterial;
          if (mat.map) mat.map.dispose();
          mat.dispose();
        });
      });

      renderer.dispose();
    };
  }, [threeConfig.enabled, threeConfig.style, threeConfig.waveSpeed, threeConfig.particleCount]);

  if (threeConfig.enabled === false) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -2,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
};

export default ThreeBackground;
